import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { Badge, Banner, Button, Card, Field, Mono, PageBody, PageHeader, Textarea } from "../ui/Field";
import { AssetPicker } from "../ui/AssetPicker";
import { EntryPicker } from "../ui/EntryPicker";

type FieldDef = { name: string; label: string; kind: string; required?: boolean; options?: { value: string; label: string }[] };
type CollectionDef = { id: string; key: string; name: string; schema_json: FieldDef[] };
type EntryDoc = { id: string; slug: string; status: string; draft_revision: number; live_revision: number | null; revision: { revision: number; data: Record<string, unknown> } | null };

export default function EntryEditor() {
  const { slug, col, entry } = useParams();
  const [def, setDef] = useState<CollectionDef | null>(null);
  const [doc, setDoc] = useState<EntryDoc | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const saveTimer = useRef<number | null>(null);

  const load = useCallback(async () => {
    if (!slug || !col || !entry) return;
    try {
      const [d, e] = await Promise.all([
        api.get<CollectionDef>(`/api/sites/${slug}/collections/${col}`),
        api.get<EntryDoc>(`/api/sites/${slug}/collections/${col}/entries/${entry}`),
      ]);
      setDef(d); setDoc(e); setDirty(false);
    } catch (e) { setErr((e as Error).message); }
  }, [slug, col, entry]);
  useEffect(() => { load(); }, [load]);

  async function save() {
    if (!doc?.revision) return;
    setBusy(true); setErr(null);
    try {
      await api.put(`/api/sites/${slug}/collections/${col}/entries/${entry}/draft`, { data: doc.revision.data });
      setDirty(false); await load();
    } catch (e) { setErr(e instanceof ApiError ? e.message : (e as Error).message); }
    finally { setBusy(false); }
  }
  async function publish() {
    if (dirty) await save();
    try { await api.post(`/api/sites/${slug}/collections/${col}/entries/${entry}/publish`); await load(); }
    catch (e) { setErr((e as Error).message); }
  }

  useEffect(() => {
    if (!dirty) return;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(save, 1500);
    return () => { if (saveTimer.current) window.clearTimeout(saveTimer.current); };
    // eslint-disable-next-line
  }, [dirty]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") { e.preventDefault(); if (dirty) save(); }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dirty]);

  function update(k: string, v: unknown) {
    setDoc((d) => d?.revision ? { ...d, revision: { ...d.revision, data: { ...d.revision.data, [k]: v } } } : d);
    setDirty(true);
  }

  if (err && !def) return <PageBody><Banner kind="error">{err}</Banner></PageBody>;
  if (!def || !doc) return <PageBody><div className="h-32 bg-[var(--color-paper-2)] animate-pulse rounded" /></PageBody>;
  const data = doc.revision?.data ?? {};

  return (
    <PageBody className="max-w-3xl">
      <Link to={`/sites/${slug}/collections/${col}`} className="text-[var(--color-muted)] hover:text-[var(--color-ink)] text-[12px] no-underline">← {def.name}</Link>
      <PageHeader
        eyebrow={`№ ${def.name} · Entry`}
        title={<>/{doc.slug}</>}
        description={
          <span className="flex items-center gap-2 flex-wrap">
            <Badge tone={doc.status === "published" ? "success" : "neutral"}>{doc.status}</Badge>
            <Mono className="text-[11px]">draft {doc.draft_revision}{doc.live_revision ? ` · live ${doc.live_revision}` : ""}</Mono>
            {dirty ? <span className="text-[var(--color-amber)] text-[12px]">· Unsaved</span> : <span className="text-[var(--color-leaf)] text-[12px]">· Saved</span>}
          </span>
        }
        actions={
          <>
            <Button variant="ghost" disabled={busy || !dirty} onClick={save}>{busy ? "Saving…" : "Save"}</Button>
            <Button onClick={publish}>Publish</Button>
          </>
        }
      />
      {err && <Banner kind="error">{err}</Banner>}

      <Card>
        {def.schema_json.map((f) => (
          <FieldInput key={f.name} field={f} value={data[f.name]} onChange={(v) => update(f.name, v)} />
        ))}
      </Card>
    </PageBody>
  );
}

function FieldInput({ field, value, onChange }: { field: FieldDef; value: unknown; onChange: (v: unknown) => void }) {
  const k = field.kind;
  const lbl = field.label + (field.required ? " *" : "");
  if (k === "boolean") return (
    <label className="flex items-start gap-2 mb-4">
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="mt-1" />
      <span className="text-[13.5px] text-[var(--color-ink-2)]">{lbl}</span>
    </label>
  );
  if (k === "rich_text" || k === "markdown") return <Textarea label={lbl} rows={8} value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} required={field.required} />;
  if (k === "number") return <Field label={lbl} type="number" value={String(value ?? "")} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} required={field.required} />;
  if (k === "date") return <Field label={lbl} type="date" value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} required={field.required} />;
  if (k === "datetime") return <Field label={lbl} type="datetime-local" value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} required={field.required} />;
  if (k === "email") return <Field label={lbl} type="email" value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} required={field.required} />;
  if (k === "url") return <Field label={lbl} type="url" value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} required={field.required} />;
  if (k === "asset") return <AssetField label={lbl} value={value as string | undefined} onChange={onChange} />;
  if (k === "reference") return <ReferenceField label={lbl} value={value as string | undefined} onChange={onChange} />;
  return <Field label={lbl} value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} required={field.required} />;
}

function AssetField({ label, value, onChange }: { label: string; value: string | undefined; onChange: (v: unknown) => void }) {
  const { slug } = useParams();
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-4">
      <span className="label-tracked block mb-2">{label}</span>
      <div className="flex gap-2">
        <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="asset id" className="flex-1 h-9 px-3 text-[13.5px] font-mono bg-[var(--color-surface)] border border-[var(--color-rule)] rounded-[var(--radius-sm)] focus:outline-none focus:border-[var(--color-ink)]" />
        <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(true)}>Pick…</Button>
      </div>
      <AssetPicker siteSlug={slug!} open={open} onClose={() => setOpen(false)} onPick={(a) => onChange(a.id)} />
    </div>
  );
}

function ReferenceField({ label, value, onChange }: { label: string; value: string | undefined; onChange: (v: unknown) => void }) {
  const { slug } = useParams();
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-4">
      <span className="label-tracked block mb-2">{label}</span>
      <div className="flex gap-2">
        <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="entry id" className="flex-1 h-9 px-3 text-[13.5px] font-mono bg-[var(--color-surface)] border border-[var(--color-rule)] rounded-[var(--radius-sm)] focus:outline-none focus:border-[var(--color-ink)]" />
        <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(true)}>Pick…</Button>
      </div>
      <EntryPicker siteSlug={slug!} open={open} onClose={() => setOpen(false)} onPick={(e) => onChange(JSON.stringify({ id: e.id, collection: e.collectionKey }))} />
    </div>
  );
}
